import React, {useEffect, useState} from 'react';

import {Group, Item} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';

import type {GetData} from './typedef';
import type {Error} from 'src/openapi/admin/models/Error';

type Props<Data> = {
    chunks: number[][],
    getData: GetData<Data>,
    onDone: (data: Data[], error: Error | null) => void,
};

export const ChunkedDownloadRequestNotification = <Data, >(
    {chunks, getData, onDone}: Props<Data>,
) => {
    const [data, setData] = useState<Data[]>([]);
    const [activeChunkId, setActiveChunkId] = useState(0);
    const [loadingError, setLoadingError] = useState<Error | null>(null);

    useEffect(() => {
        const loadChunks = async (chunkId: number) => {
            const prepareFileResult = await getData(chunks[chunkId]);

            if (prepareFileResult.payload.success) {
                const loadedData = prepareFileResult.payload.data;
                setData(prevState => [...prevState, ...loadedData]);
            } else {
                setLoadingError(prepareFileResult.payload.data as Error);
            }
            setActiveChunkId(prevState => prevState + 1);
        };

        if (activeChunkId < chunks.length && !loadingError) {
            loadChunks(activeChunkId);
        }
    }, [activeChunkId, chunks, getData, loadingError]);

    useEffect(() => {
        if (activeChunkId === chunks.length) {
            onDone(data, loadingError);
        }
    }, [activeChunkId, chunks, data, onDone, loadingError]);

    const progress = chunks.length > 0 ? (activeChunkId / chunks.length) * 100 : 0;

    return (
        <div>
            <Label>The file generating in progress...</Label>
            <Group alignItems="center">
                <Item style={{width: 300}}>
                    <div style={{height: 4, background: '#ddd', borderRadius: 2}}>
                        <div style={{
                            height: '100%',
                            width: `${Math.round(progress)}%`,
                            background: '#005fcc',
                            borderRadius: 2,
                            transition: 'width 0.3s',
                        }}
                        />
                    </div>
                </Item>
                <Item>{Math.round(progress)} %</Item>
            </Group>
        </div>
    );
};
